@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.datenschutz

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Canvas
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.TouchEvent
import org.w3c.dom.events.MouseEvent

const val SIGNATURE_CANVAS_WIDTH = 700
const val SIGNATURE_CANVAS_HEIGHT = 200

@Composable
fun SignaturDatenschutzOverlay(
    canvasId: String,
    datenschutzText: String,
    datenschutzLabel: String,
    buttonLabel: String,
    signaturHeaderText: String? = null,
    canvasHoehePx: Int,
    maxWidthPx: Int,
    overlayStyle: StyleScope.() -> Unit,
    cardStyle: StyleScope.() -> Unit,
    canvasGezeichnet: Boolean,
    onCanvasGezeichnetChange: (Boolean) -> Unit,
    datenschutzAkzeptiert: Boolean,
    onDatenschutzAkzeptiertChange: (Boolean) -> Unit,
    fehlerText: String?,
    onComplete: (String) -> Unit
) {
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            property("z-index", POPUP_ENGINE_MODAL_Z_INDEX.toString())
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Color("#15181d"))
            property("background", "radial-gradient(circle at top, #2a2f37 0%, #1d2128 42%, #15181d 100%)")
            color(Color.white)
            property("box-sizing", "border-box")
            overlayStyle()
        }
    }) {
        if (!datenschutzAkzeptiert) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    width(100.percent)
                    height(100.percent)
                    property("padding", "clamp(16px, 4vw, 40px)")
                    property("box-sizing", "border-box")
                    cardStyle()
                }
            }) {
                Div({
                    style {
                        width(100.percent)
                        property("max-width", "920px")
                        property("margin", "0 auto 18px auto")
                    }
                }) {
                    Span({
                        style {
                            color(Color.white)
                            fontSize(24.px)
                            fontWeight("bold")
                        }
                    }) { Text("Datenschutz") }
                }
                Div({
                    style {
                        property("flex", "1")
                        property("min-height", "0")
                        width(100.percent)
                        property("max-width", "920px")
                        property("margin", "0 auto")
                        property("overflow-y", "auto")
                        padding(0.px, 2.px, 20.px, 2.px)
                        property("box-sizing", "border-box")
                    }
                }) {
                    Span({
                        style {
                            color(Color("rgba(255,255,255,0.76)"))
                            fontSize(14.px)
                            lineHeight("1.7")
                            property("white-space", "pre-line")
                            display(DisplayStyle.Block)
                        }
                    }) { Text(datenschutzText) }
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(12.px)
                            marginTop(28.px)
                            padding(16.px)
                            borderRadius(10.px)
                            backgroundColor(Color.transparent)
                            property("border", "1px solid rgba(255,255,255,0.14)")
                            property("box-sizing", "border-box")
                            cursor("pointer")
                        }
                        onClick { onDatenschutzAkzeptiertChange(true) }
                    }) {
                        Div({
                            style {
                                width(18.px)
                                height(18.px)
                                minWidth(18.px)
                                borderRadius(4.px)
                                backgroundColor(Color.transparent)
                                property("border", "1px solid rgba(255,255,255,0.35)")
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.Center)
                                alignItems(AlignItems.Center)
                            }
                        })
                        Span({
                            style {
                                color(Color("rgba(255,255,255,0.82)"))
                                fontSize(14.px)
                                lineHeight("1.3")
                            }
                        }) { Text(datenschutzLabel) }
                    }
                }
            }
        } else {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    width(100.percent)
                    height(100.percent)
                    property("padding", "clamp(16px, 4vw, 40px)")
                    property("box-sizing", "border-box")
                    cardStyle()
                }
            }) {
                Div({
                    style {
                        width(100.percent)
                        property("max-width", "${SIGNATURE_CANVAS_WIDTH + 80}px")
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(18.px)
                    }
                }) {
                    Div({
                        style {
                            width(100.percent)
                            maxWidth(SIGNATURE_CANVAS_WIDTH.px)
                            property("margin", "0 auto")
                        }
                    }) {
                        if (signaturHeaderText?.isNotBlank() == true) {
                            Span({
                                style {
                                    color(DashboardTheme.TEXT_MUTED)
                                    fontSize(15.px)
                                    lineHeight("1.45")
                                    display(DisplayStyle.Block)
                                }
                            }) { Text(signaturHeaderText) }
                        }
                    }
                    Canvas(attrs = {
                        id(canvasId)
                        style {
                            width(100.percent)
                            maxWidth(SIGNATURE_CANVAS_WIDTH.px)
                            property("height", "auto")
                            property("aspect-ratio", "$SIGNATURE_CANVAS_WIDTH / $SIGNATURE_CANVAS_HEIGHT")
                            backgroundColor(Color.white)
                            property("border", "1px solid rgba(20,24,31,0.26)")
                            borderRadius(12.px)
                            property("touch-action", "none")
                            display(DisplayStyle.Block)
                            property(
                                "cursor",
                                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M12 1v22M1 12h22' stroke='%23000' stroke-width='2'/%3E%3C/svg%3E\") 12 12, crosshair"
                            )
                            property("margin", "0 auto")
                        }
                    })
                    Div({
                        style {
                            width(100.percent)
                            maxWidth(SIGNATURE_CANVAS_WIDTH.px)
                            property("margin", "0 auto")
                            display(DisplayStyle.Flex)
                            gap(12.px)
                            justifyContent(JustifyContent.FlexEnd)
                            alignItems(AlignItems.Center)
                            property("flex-wrap", "wrap")
                        }
                    }) {
                        LumaticaButton(
                            label = "Signaturfeld leeren",
                            onClick = {
                                val canvas = document.getElementById(canvasId) as? HTMLCanvasElement
                                val ctx = canvas?.getContext("2d") as? CanvasRenderingContext2D
                                if (canvas != null && ctx != null) {
                                    ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
                                }
                                onCanvasGezeichnetChange(false)
                            },
                            variant = LumaticaButtonVariant.Standard,
                            heightPx = 44,
                            paddingH = 18
                        )
                        LumaticaButton(
                            label = buttonLabel,
                            onClick = {
                                val canvas = document.getElementById(canvasId) as? HTMLCanvasElement ?: return@LumaticaButton
                                onComplete(canvas.toDataURL("image/png"))
                            },
                            variant = LumaticaButtonVariant.Action,
                            enabled = canvasGezeichnet,
                            heightPx = 44,
                            paddingH = 20,
                            fillWidth = false
                        )
                    }
                    if (fehlerText?.isNotBlank() == true) {
                        Span({
                            style {
                                color(Color("#ff9c9c"))
                                fontSize(13.px)
                            }
                        }) { Text(fehlerText) }
                    }
                }
            }
        }
    }

    LaunchedEffect(datenschutzAkzeptiert) {
        val canvas = document.getElementById(canvasId) as? HTMLCanvasElement ?: return@LaunchedEffect
        val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return@LaunchedEffect
        canvas.width = SIGNATURE_CANVAS_WIDTH
        canvas.height = SIGNATURE_CANVAS_HEIGHT
        ctx.lineWidth = 2.0
        ctx.strokeStyle = "#000000"
        ctx.asDynamic().lineCap = "round"
        ctx.asDynamic().lineJoin = "round"
        var drawing = false

        fun mappedPoint(clientX: Double, clientY: Double): Pair<Float, Float> {
            val r = canvas.getBoundingClientRect()
            val x = ((clientX - r.left) / r.width * SIGNATURE_CANVAS_WIDTH).coerceIn(0.0, SIGNATURE_CANVAS_WIDTH.toDouble())
            val y = ((clientY - r.top) / r.height * SIGNATURE_CANVAS_HEIGHT).coerceIn(0.0, SIGNATURE_CANVAS_HEIGHT.toDouble())
            return x.toFloat() to y.toFloat()
        }

        fun start(x: Float, y: Float) {
            ctx.beginPath()
            ctx.moveTo(x.toDouble(), y.toDouble())
            ctx.lineTo(x.toDouble(), y.toDouble())
            ctx.stroke()
            onCanvasGezeichnetChange(true)
        }

        fun draw(x: Float, y: Float) {
            ctx.lineTo(x.toDouble(), y.toDouble())
            ctx.stroke()
        }

        canvas.addEventListener("mousedown", {
            val ev = it.unsafeCast<MouseEvent>()
            val point = mappedPoint(ev.clientX.toDouble(), ev.clientY.toDouble())
            drawing = true
            start(point.first, point.second)
        })
        canvas.addEventListener("mouseup", { drawing = false })
        canvas.addEventListener("mouseleave", { drawing = false })
        canvas.addEventListener("mousemove", {
            if (!drawing) return@addEventListener
            val ev = it.unsafeCast<MouseEvent>()
            val point = mappedPoint(ev.clientX.toDouble(), ev.clientY.toDouble())
            draw(point.first, point.second)
        })
        canvas.addEventListener("touchstart", {
            val ev = it.unsafeCast<TouchEvent>()
            val t = ev.changedTouches.item(0) ?: return@addEventListener
            val point = mappedPoint(t.clientX.toDouble(), t.clientY.toDouble())
            drawing = true
            start(point.first, point.second)
            it.preventDefault()
        })
        canvas.addEventListener("touchend", { drawing = false; it.preventDefault() })
        canvas.addEventListener("touchmove", {
            if (!drawing) return@addEventListener
            val ev = it.unsafeCast<TouchEvent>()
            val t = ev.changedTouches.item(0) ?: return@addEventListener
            val point = mappedPoint(t.clientX.toDouble(), t.clientY.toDouble())
            draw(point.first, point.second)
            it.preventDefault()
        })
    }
}
